import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Building2, User, Plus } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Shared "Quick Add Customer" dialog used from job-creation flows.
 *
 * Matches the customer_type model used on the full Customers page:
 *   - Organisation → Company Name required, no per-customer Contact Name field
 *     (primary contacts are added later from the Customers page)
 *   - Individual  → Full Name required, no Company Name field
 *
 * Props:
 *   open, onOpenChange  – dialog visibility (controlled)
 *   api                 – authenticated axios instance (required)
 *   onCreated(customer) – called with the newly created customer on success
 */
export default function QuickAddCustomerDialog({ open, onOpenChange, api, onCreated }) {
  const [form, setForm] = useState({
    customer_type: 'organisation',
    company_name: '',
    contact_name: '',
    phone: '',
    email: '',
  });
  const [saving, setSaving] = useState(false);

  // Reset when the dialog closes
  useEffect(() => {
    if (!open) {
      setForm({ customer_type: 'organisation', company_name: '', contact_name: '', phone: '', email: '' });
      setSaving(false);
    }
  }, [open]);

  const isOrg = form.customer_type === 'organisation';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (isOrg && !form.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!isOrg && !form.contact_name.trim()) {
      toast.error('Full name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        customer_type: form.customer_type,
        company_name: isOrg ? form.company_name.trim() : null,
        contact_name: isOrg ? null : form.contact_name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
      };
      const response = await api.post('/customers', payload);
      toast.success('Customer created');
      onCreated?.(response.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="quick-add-customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Building2 className="h-5 w-5 text-[#ffd108]" />
            Quick Add Customer
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Customer type toggle */}
          <div className="space-y-2">
            <Label className="label-overline">Customer Type</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={isOrg ? 'default' : 'outline'}
                className={isOrg ? 'btn-volt' : ''}
                onClick={() => setForm((f) => ({ ...f, customer_type: 'organisation' }))}
                data-testid="quick-customer-type-organisation"
              >
                <Building2 className="h-4 w-4 mr-2" />
                Organisation
              </Button>
              <Button
                type="button"
                variant={!isOrg ? 'default' : 'outline'}
                className={!isOrg ? 'btn-volt' : ''}
                onClick={() => setForm((f) => ({ ...f, customer_type: 'individual' }))}
                data-testid="quick-customer-type-individual"
              >
                <User className="h-4 w-4 mr-2" />
                Individual
              </Button>
            </div>
          </div>

          {isOrg ? (
            <div className="space-y-2">
              <Label className="label-overline">Company Name <span className="text-red-500">*</span></Label>
              <Input
                data-testid="quick-customer-company"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="ABC Construction"
                autoFocus
                required
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="label-overline">Full Name <span className="text-red-500">*</span></Label>
              <Input
                data-testid="quick-customer-fullname"
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                placeholder="John Smith"
                autoFocus
                required
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="label-overline">Phone</Label>
              <Input
                data-testid="quick-customer-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1 234 567 890"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Email</Label>
              <Input
                data-testid="quick-customer-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="john@abc.com"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="btn-volt"
              disabled={saving}
              data-testid="quick-customer-submit"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Customer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
